import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useJourney } from '../../contexts/JourneyContext';
import api from '../../services/api';
import { Stage } from '../../types';
import LoadingOverlay from '../Common/LoadingOverlay';
import VoiceInput from '../VoiceInput/VoiceInput';
import './StageFlow.css';

// Format AI message with proper markdown-style formatting
const FormattedMessage: React.FC<{ content: string }> = ({ content }) => {
  // Split by newlines and process each line
  const lines = content.split('\n');
  
  return (
    <div className="formatted-message">
      {lines.map((line, index) => {
        // Check for numbered lists (1. 2. 3. etc.)
        if (/^\d+\.\s/.test(line)) {
          return (
            <div key={index} className="numbered-item">
              {line}
            </div>
          );
        }
        // Check for bullet points (- or * or •)
        else if (/^[-*•]\s/.test(line)) {
          return (
            <div key={index} className="bullet-item">
              {line.replace(/^[-*•]\s/, '• ')}
            </div>
          );
        }
        // Empty line
        else if (line.trim() === '') {
          return <div key={index} className="line-break" />;
        }
        // Regular paragraph
        else {
          return (
            <p key={index} className="message-paragraph">
              {line}
            </p>
          );
        }
      })}
    </div>
  );
};

const ConversationalFlow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentJourney, setCurrentJourney, isLoading, sendMessage, moveToNextStage, refreshJourney } = useJourney();
  
  const [messages, setMessages] = useState<Array<{ role: string; content: string }>>([]);
  const [hasIntroduced, setHasIntroduced] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoadingUploadedJourney, setIsLoadingUploadedJourney] = useState(false);
  const [lastJourneyId, setLastJourneyId] = useState<string | null>(null);
  const [lastStage, setLastStage] = useState<number | null>(null);
  const [isProcessingMessage, setIsProcessingMessage] = useState(false);
  const [messageCount, setMessageCount] = useState<number>(0);
  const [maxMessages] = useState<number>(25);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Check if we're coming from upload flow or edit mode
    const state = location.state as any;
    
    // Handle upload mode - journey was just created with parsed data
    if (state?.uploadMode && state?.journeyId && !currentJourney) {
      console.log('Upload mode detected, loading journey:', state.journeyId);
      setIsLoadingUploadedJourney(true);
      // Fetch the journey and set it in context
      const loadUploadedJourney = async () => {
        try {
          const api = (await import('../../services/api')).default;
          const journey = await api.getJourney(state.journeyId);
          console.log('[Upload Mode] Loaded uploaded journey:', journey);
          console.log('[Upload Mode] Journey stage:', journey.currentStage, 'Status:', journey.status);
          setCurrentJourney(journey);
          // Don't set isLoadingUploadedJourney to false here!
          // Let the second useEffect see the journey first
        } catch (error) {
          console.error('[Upload Mode] Failed to load uploaded journey:', error);
          setIsLoadingUploadedJourney(false);
          navigate('/dashboard');
        }
      };
      loadUploadedJourney();
    }
    // Handle edit mode
    else if (state?.editMode) {
      console.log('[Edit Mode] Detected - refreshing journey and resetting state');
      setIsEditMode(true);
      // Reset all state to ensure clean slate for edited stage
      setMessages([]);
      setHasIntroduced(false);
      setLastJourneyId(null);
      setLastStage(null);
      // Refresh journey to get the latest data after stage update
      refreshJourney();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]); // Only re-run when location changes, not when refreshJourney changes

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const state = location.state as any;
    const isUploadMode = state?.uploadMode && state?.journeyId;
    
    console.log('[DEBUG] useEffect triggered:', {
      hasCurrentJourney: !!currentJourney,
      isUploadMode,
      isLoadingUploadedJourney,
      shouldRedirect: !currentJourney && !isLoadingUploadedJourney && !isUploadMode
    });
    
    // Don't redirect if:
    // 1. We're in upload mode (journey is being loaded)
    // 2. We're currently loading an uploaded journey
    if (!currentJourney) {
      if (isUploadMode || isLoadingUploadedJourney) {
        console.log('[DEBUG] No journey yet, but in upload mode or loading - waiting...');
        return; // Still loading
      }
      console.log('[DEBUG] Redirecting to dashboard - no journey and not in upload mode');
      navigate('/dashboard');
      return;
    }
    
    
    // Journey is loaded, we can stop the loading state
    if (isLoadingUploadedJourney) {
      console.log('[DEBUG] Journey loaded, clearing loading flag');
      setIsLoadingUploadedJourney(false);
    }
    
    console.log('[DEBUG] Journey exists, processing...');

    // Track journey and stage changes
    const journeyChanged = currentJourney.id !== lastJourneyId;
    const stageChanged = currentJourney.currentStage !== lastStage;

    if (journeyChanged) {
      // Brand new journey or resumed journey (including edit mode after refresh)
      console.log('[Journey Change] Setting up new journey:', currentJourney.id, 'Stage:', currentJourney.currentStage);
      setLastJourneyId(currentJourney.id);
      setLastStage(currentJourney.currentStage);
      setHasIntroduced(false);
      // Don't clear messages if in edit mode - they were already cleared
      if (!isEditMode) {
        setMessages([]);
      }
      return;
    }

    if (stageChanged && !isEditMode) {
      // User moved to a different stage - clear messages and show intro only
      // But don't do this in edit mode, as we're explicitly loading that stage
      console.log('[Stage Change] Stage changed to:', currentJourney.currentStage);
      setLastStage(currentJourney.currentStage);
      setMessages([]);
      setHasIntroduced(false);
      return;
    }

    // Load stage introduction (with or without history)
    if (!hasIntroduced && currentJourney) {
      const loadIntroduction = async () => {
        let introMessage: string;
        
        console.log('[Dynamic Question] Current stage:', currentJourney.currentStage, 'Projects stage value:', Stage.Projects);
        
        // For Projects stage (3), get dynamic AI-generated first question
        if (currentJourney.currentStage === 3) {
          console.log('[Dynamic Question] Attempting to fetch dynamic question for journey:', currentJourney.id);
          console.log('[Spinner] Setting isProcessingMessage to TRUE');
          setIsProcessingMessage(true); // Show loading while fetching dynamic question
          
          // Give React time to render the spinner
          await new Promise(resolve => setTimeout(resolve, 100));
          
          try {
            const api = (await import('../../services/api')).default;
            const response = await api.getDynamicFirstQuestion(currentJourney.id);
            console.log('[Dynamic Question] Received response:', response);
            introMessage = response.question;
          } catch (error) {
            console.error('[Dynamic Question] Failed to get dynamic question, using fallback:', error);
            introMessage = getStageIntroduction(currentJourney.currentStage);
          } finally {
            console.log('[Spinner] Setting isProcessingMessage to FALSE');
            setIsProcessingMessage(false);
          }
        } else {
          console.log('[Dynamic Question] Not Projects stage, using static intro');
          introMessage = getStageIntroduction(currentJourney.currentStage);
        }
        
        // Filter conversation history to only include messages from the CURRENT stage
        const currentStageHistory = currentJourney.conversationHistory?.filter(
          msg => msg.stage === currentJourney.currentStage
        ) || [];
        
        console.log(`[Load History] Stage ${currentJourney.currentStage}: Found ${currentStageHistory.length} messages in history`);
        console.log('[Load History] Edit mode:', isEditMode);
        
        if (currentStageHistory.length > 0) {
          // Resuming with history for current stage: show intro + filtered history
          const formattedHistory = currentStageHistory.map(msg => ({
            role: msg.role,
            content: msg.content
          }));
          
          setMessages([
            { role: 'assistant', content: introMessage },
            ...formattedHistory
          ]);
          console.log(`[Load History] Loaded introduction + ${formattedHistory.length} messages from stage ${currentJourney.currentStage}`);
        } else {
          // Fresh stage or no history for current stage: just show introduction
          setMessages([{ role: 'assistant', content: introMessage }]);
          console.log('[Load History] No history for this stage, showing fresh introduction');
        }
        setHasIntroduced(true);
      };
      
      loadIntroduction();
    }
    // We intentionally avoid adding more dependencies here to prevent
    // re-running the introduction logic unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentJourney, navigate, hasIntroduced, lastJourneyId, lastStage, isLoadingUploadedJourney]);

  const getStageIntroduction = (stage: Stage): string => {
    // Note: Stages 0-2 (Personal, Education, Employment) now use forms, not conversational flow
    // This function only handles stages 3-4 (Projects, Skills)
    switch (stage) {
      case Stage.Projects:
        return `Perfect! Now let's dive deep into your projects. This is where you can really shine!

For each significant project you've worked on, please share:

1. What was the project about?
2. What was your specific role and contribution?
3. What technologies, tools, or methodologies did you use?
4. What challenges did you face and how did you overcome them?
5. What was the measurable impact or outcome?
6. What did you learn from this experience?

Think of this as interview preparation - I'll ask detailed follow-up questions to help you articulate your expertise and problem-solving skills. Let's start with your most impressive project!`;

      case Stage.SkillsCertifications:
        return `Almost there! Let's capture your skills and additional qualifications.

Please share information about:

1. What are your key technical or professional skills?
2. Do you have any professional certifications? (Include issuer, date, credential ID if available)
3. Have you received any awards or recognition?
4. Do you have any publications or research papers?
5. Have you done any volunteer work or community service?
6. Any other relevant information you'd like to include?

This is your chance to showcase everything that makes you a great candidate!`;

      default:
        return 'Let\'s continue building your resume!';
    }
  };

  const getStageTitle = (stage: Stage): string => {
    // Note: Stages 0-2 (Personal, Education, Employment) now use forms, not conversational flow
    const titles: { [key: number]: string } = {
      [Stage.Projects]: 'Projects & Certifications',
      [Stage.SkillsCertifications]: 'Skills & Achievements',
      [Stage.Review]: 'Review Your Information',
    };
    return titles[stage] || 'Deep Dive Conversation';
  };

  const handleMessageSubmit = async (message: string | Blob) => {
    try {
      let displayMessage = '';
      
      // Determine display message
      if (typeof message === 'string') {
        displayMessage = message;
      } else {
        displayMessage = '[Voice message]';
      }
      
      // Add user message to display (temporarily - will be replaced with transcribed text for voice)
      setMessages((prev) => [...prev, { role: 'user', content: displayMessage }]);

      // Show loading state
      console.log('[Spinner] User sent message, setting isProcessingMessage to TRUE');
      setIsProcessingMessage(true);

      // Send to AI (handles both text and audio)
      const response = await sendMessage(message);

      // Update messages: replace the placeholder with actual transcribed text for voice messages
      setMessages((prev) => {
        const newMessages = [...prev];
        if (response.transcribedText && typeof message !== 'string') {
          // Replace the last user message (placeholder) with the actual transcribed text
          newMessages[newMessages.length - 1] = { 
            role: 'user', 
            content: response.transcribedText 
          };
        }
        // Add AI response
        return [...newMessages, { role: 'assistant', content: response.aiResponse }];
      });
      
      // Increment message count
      setMessageCount(prev => prev + 1);
      setRateLimitError(null);
    } catch (error: any) {
      console.error('Failed to send message:', error);
      
      // Handle rate limit error (429)
      if (error.response?.status === 429) {
        const errorMsg = error.response?.data?.error || 'You\'ve reached the conversation limit for this stage.';
        setRateLimitError(errorMsg);
        // Remove the placeholder user message since it failed
        setMessages((prev) => prev.slice(0, -1));
      } else {
        alert('Failed to process your message. Please try again.');
      }
    } finally {
      console.log('[Spinner] AI response received, setting isProcessingMessage to FALSE');
      setIsProcessingMessage(false);
    }
  };

  const handleNextStage = async () => {
    if (!currentJourney) return;
    
    try {
      const nextStage = currentJourney.currentStage + 1;
      console.log('[Next Stage] Moving from stage', currentJourney.currentStage, 'to stage', nextStage);
      
      // If in edit mode, go back to review instead of forward
      if (isEditMode) {
        console.log('[Edit Mode] Completing edit, re-extracting data and returning to review');
        // Set stage to SkillsCertifications (4), so moveToNextStage will move to Review (5) and trigger extraction
        await api.updateJourneyStage(currentJourney.id, 4);
        // Trigger data extraction by moving from stage 4 to 5 (Review)
        await api.moveToNextStage(currentJourney.id);
        // Refresh journey to get the newly extracted data
        await refreshJourney();
        // Navigate back to review
        console.log('[Edit Mode] Data re-extracted, navigating back to review');
        navigate('/journey/review');
        return;
      }
      
      await moveToNextStage();
      
      setMessages([]);
      setHasIntroduced(false);

      // Navigate based on what the NEXT stage will be (before the state updates)
      if (nextStage === Stage.Review) {
        console.log('Navigating to review page');
        // Give React a moment to update the context state
        setTimeout(() => navigate('/journey/review'), 100);
      }
      else if (nextStage === Stage.TemplateSelection) {
        console.log('Navigating to template selection');
        setTimeout(() => navigate('/journey/templates'), 100);
      }
    } catch (error) {
      console.error('Failed to move to next stage:', error);
      alert('Failed to move to next stage. Please try again.');
    }
  };

  if (!currentJourney) {
    return <div className="loading">Loading...</div>;
  }

  // Calculate progress for deep-dive stages (3 & 4 only)
  // Stage 3 (Projects) = Step 1 of 2
  // Stage 4 (Skills) = Step 2 of 2
  const deepDiveStep = currentJourney.currentStage === Stage.Projects ? 1 : 2;
  const progress = (deepDiveStep / 2) * 100;

  console.log('[Spinner] Rendering component, isProcessingMessage:', isProcessingMessage);

  return (
    <div className="stage-flow">
      <div className="stage-header">
        <h1>{getStageTitle(currentJourney.currentStage)}</h1>
        <p className="text-secondary" style={{ marginTop: 'var(--spacing-2)', marginBottom: 'var(--spacing-4)' }}>
          Practice articulating your experience—these questions prepare you for real interviews
        </p>
        
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }}></div>
        </div>
        <p className="progress-text">
          Deep Dive: Step {deepDiveStep} of 2
        </p>
      </div>

      <div className="conversation-container">
        <div className="messages">
          {messages.map((msg, index) => (
            <div
              key={index}
              className={`message ${msg.role === 'user' ? 'message-user' : 'message-ai'}`}
            >
              <div className="message-content">
                {msg.role === 'assistant' ? (
                  <FormattedMessage content={msg.content} />
                ) : (
                  msg.content
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-section">
          {/* Message count and rate limit warning */}
          <div className="message-count-info">
            <span className="message-count">
              {messageCount} / {maxMessages} messages
            </span>
            {messageCount >= maxMessages * 0.8 && messageCount < maxMessages && (
              <span className="message-warning">
                You're approaching the message limit. Consider moving to the next stage soon.
              </span>
            )}
          </div>

          {/* Rate limit error */}
          {rateLimitError && (
            <div className="alert alert-error rate-limit-alert">
              <p>{rateLimitError}</p>
              <button 
                onClick={handleNextStage}
                className="btn btn-primary"
              >
                Move to Next Stage
              </button>
            </div>
          )}

          <VoiceInput 
            onSubmit={handleMessageSubmit} 
            isLoading={isLoading || isProcessingMessage || !!rateLimitError}
            placeholder="Type your response or click the mic to speak..."
          />

          <div className="stage-actions">
            <button
              onClick={handleNextStage}
              className="btn-primary btn-next-stage"
              disabled={isLoading || isProcessingMessage}
            >
              I've shared everything for this section →
            </button>
          </div>
        </div>
      </div>

      {/* AI Processing Overlay */}
      {isProcessingMessage && (
        <LoadingOverlay message="Thinking..." />
      )}
    </div>
  );
};

export default ConversationalFlow;

